import { v4 as uuid } from 'uuid';

import CallbackProcessingError from './CallbackProcessingError';
import { MEMORY_DB_GUARD_POLICY, StoreType } from './constants';
import {
  GET_ITEM_COUNT,
  VISIBILITY_TIMEOUT,
} from './defaults';
import MemoryDbEventCountGuard from './MemoryDbEventCountGuard';
import {
  AddOptions,
  ItemWrapperType,
  ProcessFnType,
  Resilience,
  ResilienceOptions,
} from './types';
import {
  createOptionsWithDefaults
} from './util';

export default class MemoryDb<T> implements Resilience<T> {

  private memoryStore: ItemWrapperType<T>[];
  private options: Required<ResilienceOptions>;
  private globalEventLimitGuard: MemoryDbEventCountGuard<T>;
  private product: string;

  constructor(product: string, options: ResilienceOptions = {}) {
    this.memoryStore = [];
    this.options = createOptionsWithDefaults(options, this.storeType());
    if (!product || product.length === 0 || typeof product!== 'string') {
      throw new Error('Invalid product provided');
    }
    this.product = product;
    /**
     * This class will enforce the number of analytics events we can store in our MemoryDb
     */
    this.globalEventLimitGuard = new MemoryDbEventCountGuard(
      this.options.maxEventLimit,
      this.memoryStore
    );
  }

  /**
   * This method is used mainly to write new events to MemoryDB and uses the MEMORY_DB_GUARD_POLICY.EVICT_OLDEST_IF_LIMIT_EXECEEDED policy
   * to removes oldest events when limit is reached.
   */
  addItem(
    item: T,
    {
      id,
      retryAttempts,
      timeToProcessOffset,
    }: AddOptions = {}
  ): Promise<string> {
    const storedValue: ItemWrapperType<T> = {
      item,
      id: id || uuid(),
      retryAttempts: retryAttempts || 0,
      timeToBeProcessedAfter: Date.now() + (timeToProcessOffset || 0),
      timeAdded: Date.now(),
      product: this.product,
    };
    // Delegated responsiblity to event limit guard to insert to memory stored
    this.globalEventLimitGuard.insertItemsToMemoryStore([storedValue], MEMORY_DB_GUARD_POLICY.EVICT_OLDEST_IF_LIMIT_EXECEEDED);
    return Promise.resolve(storedValue.id);
  }

  /**
   * This method is used mainly to write old events to MemoryDB; moving events from LocalStorage to Memory.
   *
   * This method uses the MEMORY_DB_GUARD_POLICY.EVICT_OLDEST_IF_LIMIT_EXECEEDED policy
   * to stop exceeding the event count limit.
   *
   * This does not mean we throw away the events in LocalStorage, they will still be there, but we wont take them just yet.
   */
  massAddItems(items: ItemWrapperType<T>[]): Promise<void> {
    // Delegated responsiblity to event limit guard to insert to memory stored
    this.globalEventLimitGuard.insertItemsToMemoryStore(items, MEMORY_DB_GUARD_POLICY.ABANDON_IF_LIMIT_WILL_EXECEED);
    return Promise.resolve();
  }

  getItems(count: number = GET_ITEM_COUNT): Promise<ItemWrapperType<T>[]> {
    return Promise.resolve(this.synchronousGetItems(count));
  }

  private synchronousGetItems(count: number = GET_ITEM_COUNT): ItemWrapperType<T>[] {
    const fixedCount = count > 0 ? count : GET_ITEM_COUNT;
    const now = Date.now();
    const wrappedItems: ItemWrapperType<T>[] = [];

    for (let wrappedItem of this.memoryStore) {
      if (wrappedItem.timeToBeProcessedAfter <= now) {
        wrappedItems.push({
          ...wrappedItem
        });
        // Mutates the item in the memoryStore array, but not whats inside of wrappedItems
        wrappedItem.timeToBeProcessedAfter += VISIBILITY_TIMEOUT;
        wrappedItem.retryAttempts += 1;
      }

      if (wrappedItems.length >= fixedCount) {
        break;
      }
    }

    return wrappedItems;
  }

  deleteItems(itemIds: string[]): Promise<void> {
    this.memoryStore = this.memoryStore.filter(
      item => !itemIds.includes(item.id)
    );
    return Promise.resolve(void 0);
  }

  getItemCount(): Promise<number> {
    const now = Date.now();
    const count = this.memoryStore.filter(item => item.timeToBeProcessedAfter <= now).length;
    return Promise.resolve(count);
  }

  async processItems(processFn: ProcessFnType<T>, count?: number): Promise<void> {
    const items = this.synchronousGetItems(count);
    const itemIds = items.map(i => i.id);
    try {
      await processFn(items);
      return this.deleteItems(itemIds);
    } catch (error) {
      throw new CallbackProcessingError(error);
    }
  }

  public storeType(): StoreType {
    return StoreType.MEMORY;
  }
}
